import type { App } from 'vue';
import { destroyApiClient, initApiClient, type ApiClientConfig } from './api';
import { type IService } from './base';
import { initBuriedService, type BuriedServiceConfig } from './buried';
import { initI18nService, resetGlobalI18nService, type I18nServiceConfig } from './i18n';
import { initOssService, type OssConfig } from './oss';
import { destroyRouterService, initRouterService, type RouterServiceConfig } from './router';
import { initSSEService, type SSEConfig } from './sse';
import { initStoreService, resetGlobalStoreService, type StoreServiceConfig } from './store';
import { initWebSocketService, type WebSocketConfig } from './websocket';

// 应用配置接口
export interface AppConfig {
  api: ApiClientConfig;
  buried?: BuriedServiceConfig;
  i18n?: I18nServiceConfig;
  router?: RouterServiceConfig;
  store?: StoreServiceConfig;
  oss?: OssConfig;
  sse?: SSEConfig;
  websocket?: WebSocketConfig;
}

// 应用初始化服务
export class AppInitService {
  private app: App;
  private config: AppConfig;
  private services: IService[] = [];
  private apiClient: Awaited<ReturnType<typeof initApiClient>> | null = null; // SSR 中存储 ApiClient 实例用于清理

  constructor(app: App, config: AppConfig) {
    this.app = app;
    this.config = config;
  }

  // 初始化所有服务
  async initialize(): Promise<void> {
    // 初始化所有服务
    await this.initializeServices();
  }

  // 初始化服务
  private async initializeServices(): Promise<void> {
    // 初始化 Store 服务
    const storeService = await initStoreService(this.config.store);
    this.services.push(storeService);

    // 安装 Store 到 Vue 应用
    storeService.install(this.app);

    // 初始化路由服务
    if (this.config.router) {
      const routerService = await initRouterService(this.config.router);
      this.services.push(routerService);

      // 安装路由服务到 Vue 应用
      routerService.install(this.app);
    }

    // 初始化 API 客户端
    // SSR 环境下存储实例以便清理
    this.apiClient = await initApiClient(this.config.api);

    // 初始化埋点服务（仅在客户端）
    if (this.config.buried && !import.meta.server) {
      const buriedService = await initBuriedService(this.config.buried);
      this.services.push(buriedService);

      // 安装埋点服务到 Vue 应用
      buriedService.install(this.app);
    }

    // 初始化 i18n 服务
    if (this.config.i18n) {
      const i18nService = await initI18nService(this.config.i18n);
      this.services.push(i18nService);

      // 安装 i18n 到 Vue 应用
      i18nService.install(this.app);
    }

    // 初始化 OSS 服务（仅在客户端）
    if (this.config.oss && !import.meta.server) {
      const ossService = await initOssService();
      this.services.push(ossService);
    }

    // 初始化 SSE 服务（仅在客户端）
    if (this.config.sse && !import.meta.server) {
      const sseService = await initSSEService(this.config.sse);
      this.services.push(sseService);
    }

    // 初始化 WebSocket 服务（仅在客户端）
    if (this.config.websocket && !import.meta.server) {
      const websocketService = await initWebSocketService(this.config.websocket);
      this.services.push(websocketService);
    }
  }

  // 获取配置
  getConfig(): AppConfig {
    return this.config;
  }

  // 获取服务状态
  getServiceStatus() {
    return this.services.map(service => ({
      name: service.name,
      state: service.state,
    }));
  }

  // 销毁所有服务
  async destroy(): Promise<void> {
    for (const service of this.services) {
      await service.destroy();
    }

    // SSR 环境下清理 ApiClient 实例（清理拦截器）
    if (import.meta.server && this.apiClient) {
      this.apiClient.destroy();
      this.apiClient = null;
    }

    // 清理引用，避免内存泄露
    this.services = [];
    // @ts-expect-error - 清理 app 引用
    this.app = null;
    // @ts-expect-error - 清理 config 引用
    this.config = null;

    // SSR 环境下清理全局服务实例，避免跨请求共享
    if (import.meta.server) {
      // 清理全局 I18n 服务实例
      resetGlobalI18nService();

      // 清理全局 Store 服务实例（虽然 SSR 中创建新实例，但确保清理）
      resetGlobalStoreService?.();

      // 清理全局路由服务实例
      await destroyRouterService?.();

      // 清理全局 API 客户端实例（SSR 中每个请求创建新实例，不设置 globalApiClient）
      // 但为了安全起见，如果存在全局引用则清理
      await destroyApiClient?.();

      // 注意：SSE、WebSocket、OSS 服务在 SSR 环境下不会初始化（会抛出错误）
      // 所以不需要在 SSR 中清理它们
    }
  }
}

// 便捷的初始化函数
export const initializeApp = async (app: App, config: AppConfig): Promise<AppInitService> => {
  const appInitService = new AppInitService(app, config);
  await appInitService.initialize();
  return appInitService;
};

// 导出服务类型
export type { IService } from './base';

// 导出 SSE 服务
export { getSSEService, useSSE } from './sse';
export type { SSEConfig, SSEErrorCallback, SSEMessageCallback, SSEServiceConfig } from './sse';

// 导出 WebSocket 服务
export { getWebSocketService, useWebSocket } from './websocket';
export type { MessageCallback, WebSocketConfig, WebSocketServiceConfig } from './websocket';

//
